import { useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import IconEyeOpen from "../icons/IconEyeOpen";
import IconEyeShut from "../icons/IconEyeShut";
import Spacer from "./Spacer";

export default function FormInput({
  type,
  min,
  max,
  step,
  selectAllowEmptyOption = true,
  selectOptions = [], // In case it is select box
  title = "",
  showTitle = true,
  placeholder = "",
  value = "",
  selectedImageUrl = null, // If input type is image and has URL
  hasObscureText = false, // If set to true, a hide or show icon is shown
  obscureText = true, // Start always with hidden text
  onChanged,
  language,
  onLanguageChanged,
}) {
  const [localObscureText, setLocalObscureText] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState(value || []);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const handleMultiselectChange = (e, optionValue) => {
    const updatedSelection = e.target.checked
      ? [...selectedOptions, optionValue]
      : selectedOptions.filter((val) => val !== optionValue);

    setSelectedOptions(updatedSelection);
    onChanged(updatedSelection);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      onChanged(file);
      setImagePreview(URL.createObjectURL(file)); // Set the preview URL
      onChanged(file);
      setSelectedFileName(file.name);
    }
  };

  useEffect(() => {
    setLocalObscureText(obscureText);
    if (selectedImageUrl && imagePreview === null) {
      setImagePreview(selectedImageUrl);
    }
  }, [obscureText]);

  return (
    <div className="input">
      {(type === "number" || type === "text" || type === "email") && (
        <>
          {title && <span className="input-title">{title}</span>}
          <input
            className={`${hasObscureText == true ? "password" : ""}`}
            type={
              hasObscureText == true && localObscureText === true
                ? "password"
                : type
            }
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => {
              var value = e.target.value;
              if (min) {
                if (value < min) {
                  return;
                }
              }
              if (max) {
                if (value > max) {
                  return;
                }
              }
              // Truncate value to match the number of decimals in step
              if (step) {
                const decimals = step.toString().includes(".")
                  ? step.toString().split(".")[1].length
                  : 0;
                value = parseFloat(value).toFixed(decimals);
              }
              onChanged(value);
            }}
            autoComplete="off"
          />
          {hasObscureText === true && (
            <span
              className="input-icon"
              onClick={() => setLocalObscureText(!localObscureText)}
            >
              {localObscureText == true ? <IconEyeOpen /> : <IconEyeShut />}
            </span>
          )}
        </>
      )}

      {type === "textarea" && (
        <>
          {title && <span className="input-title">{title}</span>}
          <textarea
            value={value}
            onChange={(e) => {
              onChanged(e.target.value);
            }}
            autoComplete="off"
          >
            {value}
          </textarea>
        </>
      )}

      {type === "select" && (
        <div>
          {title && showTitle && (
            <span className="input-title select-input-title">{title}</span>
          )}
          <Spacer height="var(--spaceXS)" />
          <select
            defaultValue={value}
            onChange={(e) => onChanged(e.target.value)}
          >
            {selectAllowEmptyOption && <option value="">Select {title}</option>}
            {selectOptions.length > 0 &&
              selectOptions.map((selectOption, index) => {
                return (
                  <option
                    key={index}
                    value={selectOption.uuid}
                    selected={selectOption.selected}
                  >
                    {selectOption.title}
                    {selectOption.name}
                    {selectOption.code && ` (${selectOption.code})`}
                    {selectOption.symbol}
                  </option>
                );
              })}
          </select>
        </div>
      )}

      {type === "checkbox" && (
        <label className="checkbox-label">
          {title && <span className="checkbox-title">{title}</span>}
          <input
            autoComplete="off"
            type="checkbox"
            checked={value}
            onChange={(e) => onChanged(e.target.checked)}
          />
        </label>
      )}

      {type === "multiselect" && (
        <div className="multiselect">
          {title && <span className="checkbox-title">{title}</span>}
          {selectOptions.length > 0 &&
            selectOptions.map((selectOption, index) => {
              <label key={index} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectOption.value}
                  onChange={(e) => onChanged(e.target.checked)}
                />
                {selectOption.title && (
                  <span className="checkbox-title">{selectOption.title}</span>
                )}
              </label>;
            })}
        </div>
      )}

      {type === "image" && (
        <label className="image-label">
          {title && <span className="image-title">{title}</span>}
          <input
            type="file"
            accept="image/jpeg, image/png, image/jpg"
            value={value}
            onChange={handleFileChange}
          />
          {imagePreview && (
            <>
              <img
                src={imagePreview}
                alt="Image Preview"
                className="image-preview"
              />
              {selectedFileName && (
                <span className="file-name-display">
                  {selectedFileName}
                  <div
                    className="remove"
                    onClick={() => {
                      onChanged(null);
                      setSelectedFileName("");
                      setImagePreview(selectedImageUrl);
                    }}
                  >
                    x
                  </div>
                </span>
              )}
            </>
          )}
        </label>
      )}

      {type === "rich_editor" && (
        <div style={{ width: "100%" }}>
          {title && <div className="input-title">{title}</div>}
          <Spacer height="var(--spaceXS)" />
          <select
            value={language}
            onChange={(e) => onLanguageChanged(e.target.value)}
          >
            <option value="markdown">Text (Markdown)</option>
            <option value="html">HTML</option>
            <option value="javascript">JavaScript</option>
            <option value="css">CSS</option>
            <option value="php">PHP</option>
            <option value="python">Python</option>
          </select>
          <Spacer height="var(--spaceS)" />
          <Editor
            theme="vs-dark"
            height="40vh"
            width="100%"
            language={language}
            defaultValue="// Start writing something awesome :)"
            value={value}
            onChange={(value) => onChanged(value)}
          />
        </div>
      )}
    </div>
  );
}
